// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { AfterViewInit, Component, HostListener, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '@environments/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Project, ProjectSummary } from 'lfx-pcc';
import { compact, isEmpty, map as loMap, orderBy } from 'lodash';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, map, mergeMap, Observable, of, retry, switchMap, take, tap } from 'rxjs';

import { ContactSupportComponent } from './shared/components/contact-support/contact-support.component';
import { FeatureFlagService } from './shared/services/feature-flag.service';
import { MetaService } from './shared/services/meta.service';
import { ProjectService } from './shared/services/project.service';
import { ReportService } from './shared/services/report.service';
import { UserService } from './shared/services/user.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { IntercomService } from './shared/services/intercom.service';

@UntilDestroy({ checkProperties: true })
@Component({
  standalone: false,
  selector: 'lfx-root',

  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  public user$: Observable<User | null | undefined>;
  public project$: Observable<Project | null>;
  public projectID$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>('');
  public currentProjectID: string = '';
  public currentProjectID$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>('');
  public hideHeader: boolean = false;
  public hideSidebar: boolean = false;
  public isHealthMetrics: boolean = false;

  private userService: UserService = inject(UserService);
  private authService: AuthService = inject(AuthService);
  private router: Router = inject(Router);
  private metaService: MetaService = inject(MetaService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private dialogService: DialogService = inject(DialogService);
  private projectService: ProjectService = inject(ProjectService);
  private featureService: FeatureFlagService = inject(FeatureFlagService);
  private reportService: ReportService = inject(ReportService);
  private messageService: MessageService = inject(MessageService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private intercomService: IntercomService = inject(IntercomService);
  private intercomBooted: boolean = false;

  public constructor() {
    this.currentProjectID$.pipe(distinctUntilChanged()).subscribe((projectID) => {
      if (projectID && projectID !== 'undefined' && projectID !== 'null') {
        this.projectService.currentProject$.next(null);
        this.project$ = this.userService.getProjectPermissions(projectID).pipe(
          retry({ count: 2, delay: 3000 }),
          catchError((error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.message,
            });

            this.router.navigate(['/not-available'], {
              queryParams: { projectId: projectID },
            });
            return of(error);
          }),
          switchMap(() => this.projectService.getProject(projectID, true, true))
        );
      }
    });
  }

  @HostListener('window:keydown', ['$event']) public handleKeyDown(event: KeyboardEvent) {
    if (event.metaKey && event.key === 'k') {
      this.reportService
        .clearCache(this.currentProjectID)
        .pipe(take(1))
        .subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Cache Cleared',
            detail: 'Report cache cleared successfully',
          });
        });
    }
  }

  public ngOnInit(): void {
    this.loadLFxHeaderScriptAndLinks();
  }

  public ngAfterViewInit(): void {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.hideHeader = false;
        this.hideSidebar = false;
        const projectPath = e.url.split('?')[0].split('/');
        this.redirectToV1(projectPath, e.url);
        if (projectPath && projectPath.length >= 2) {
          this.isHealthMetrics = projectPath.includes('health-metrics');

          if (
            (projectPath && projectPath.includes('all-projects')) ||
            projectPath.includes('not-found') ||
            projectPath.includes('global-surveys') ||
            projectPath.includes('global-committee-audit') ||
            projectPath.includes('project-formation') ||
            projectPath.includes('changelog') ||
            projectPath.includes('not-available') ||
            projectPath.includes('youtube-registration-failed')
          ) {
            this.currentProjectID = '';
            this.hideHeader = true;
            this.hideSidebar = projectPath.includes('not-available') || projectPath.includes('youtube-registration-failed');
          } else {
            this.currentProjectID = projectPath[2];
          }
        }
      }

      if (e instanceof NavigationEnd) {
        window.scrollTo(0, 0);
        this.hideHeader = false;
        const projectPath = e.urlAfterRedirects.split('?')[0].split('/');

        // Track page view in analytics
        const segments = e.urlAfterRedirects.split('/').filter(Boolean);
        const pageName = segments.length > 0 ? segments[segments.length - 1] : 'Home';
        this.analyticsService.trackPage(pageName, {
          path: e.urlAfterRedirects,
          url: window.location.href,
        });
        this.redirectToV1(projectPath, e.urlAfterRedirects);
        this.isHealthMetrics = projectPath.includes('health-metrics');
        if (
          projectPath &&
          (projectPath.includes('all-projects') ||
            projectPath.includes('not-found') ||
            projectPath.includes('global-surveys') ||
            projectPath.includes('global-committee-audit') ||
            projectPath.includes('project-formation') ||
            projectPath.includes('changelog') ||
            projectPath.includes('not-available') ||
            projectPath.includes('youtube-registration-failed'))
        ) {
          this.currentProjectID = '';
          this.hideHeader = true;
          this.hideSidebar = projectPath.includes('not-available') || projectPath.includes('youtube-registration-failed');
        } else {
          if (projectPath.length >= 3) {
            const routeProjectID = projectPath[2];
            if (routeProjectID === 'undefined' || routeProjectID === 'null') {
              this.router.navigate(['/all-projects']);
              return;
            }
            this.currentProjectID = routeProjectID;
          }
        }

        this.projectID$.next(this.currentProjectID || null);
        this.currentProjectID$.next(this.currentProjectID || null);
        if (this.currentProjectID) {
          this.userService.setRecentlyViewedProjects(this.currentProjectID);
          localStorage.setItem('currentProjectID', this.currentProjectID);
        }
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.rootRoute(this.activatedRoute)),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route: ActivatedRoute) => route.data)
      )
      .subscribe((event) => {
        this.metaService.setTitle(event.title);
      });
  }

  public onContactSupport(event: ToastMessageOptions): void {
    this.dialogService.open(ContactSupportComponent, {
      header: 'Contact Support',
      closable: true,
      dismissableMask: true,
      modal: true,
      styleClass: 'contact-support-dialog',
      data: {
        projectID: this.currentProjectID,
        error: event.data,
      },
    });
  }

  private rootRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private loadLFxHeaderScriptAndLinks() {
    this.setScript().then(() => {
      datadogRum.init({
        applicationId: '2171b650-b0c8-4236-9249-0b91170b4df9',
        clientToken: 'pub4dcd440df14efd4c777e6ca817056c42',
        site: 'datadoghq.com',
        service: 'lfx-pcc-spa',
        env: environment.datadogEnv,
        traceSampleRate: environment.datadogEnv ? 100 : 0,
        sessionSampleRate: environment.datadogEnv ? 100 : 0,
        sessionReplaySampleRate: environment.datadogEnv ? 100 : 0,
        telemetrySampleRate: environment.datadogEnv ? 100 : 0,
        trackUserInteractions: true,
        allowedTracingUrls: environment.traceOrigins,
        defaultPrivacyLevel: 'allow',
        version: environment?.appVersion,
      });

      this.user$ = this.authService.user$.pipe(
        tap((user) => {
          if (environment.datadogEnv) {
            datadogRum.startSessionReplayRecording();
          }

          if (user) {
            // Wait for LaunchDarkly to initialize before checking flags
            this.featureService
              .initialize(user as User)
              .then(() => {
                datadogRum.setUser({
                  id: user[environment.auth0UsernameClaim],
                  email: user.email,
                  name: user.name,
                });

                // Identify user in Segment Analytics
                this.analyticsService.identifyAuth0User(user);

                // Track authentication event
                this.analyticsService.trackEvent('User Authenticated', {
                  method: 'auth0',
                  timestamp: new Date().toISOString(),
                });

                // Boot Intercom with Auth0 user data (controlled by LaunchDarkly feature flag)
                if (!this.intercomBooted && environment.intercomId && this.featureService.getBooleanFlag('enable-intercom', false)()) {
                  const intercomJwt = user[environment.auth0IntercomClaim];
                  const userId = user[environment.auth0UsernameClaim];

                  if (userId && intercomJwt) {
                    this.intercomBooted = true;
                    this.intercomService
                      .boot({
                        api_base: environment.intercomApiBase,
                        app_id: environment.intercomId,
                        intercom_user_jwt: intercomJwt,
                        user_id: userId,
                        name: user.name,
                        email: user.email,
                      })
                      .catch((error) => {
                        console.error('AppComponent: Failed to boot Intercom with JWT', error);
                        this.intercomBooted = false;
                      });
                  } else {
                    console.warn('AppComponent: Intercom not booted - missing required claim(s)', {
                      hasUserId: !!userId,
                      hasIntercomJwt: !!intercomJwt,
                    });
                  }
                } else if (!this.intercomBooted && environment.intercomId) {
                  console.info('Intercom: Disabled by LaunchDarkly feature flag');
                }
              })
              .catch((error) => {
                console.error('AppComponent: LaunchDarkly initialization failed, continuing with disabled features', error);
                this.intercomBooted = false;

                // Continue with core user setup even if LaunchDarkly fails
                datadogRum.setUser({
                  id: user[environment.auth0UsernameClaim],
                  email: user.email,
                  name: user.name,
                });

                // Identify user in Segment Analytics
                this.analyticsService.identifyAuth0User(user);

                // Track authentication event
                this.analyticsService.trackEvent('User Authenticated', {
                  method: 'auth0',
                  timestamp: new Date().toISOString(),
                });
              }); // End of featureService.initialize().then().catch()
          } else {
            // User logged out - shutdown Intercom to clear session and prevent identity leakage
            if (this.intercomBooted) {
              this.intercomService.shutdown();
              this.intercomBooted = false;
            }
          }
        })
      );
    });
  }

  /**
   * filterProjects - filter projects and it's sub projects based on the search string
   * This is different from the filtering on the admin dashboard as this should not include the child projects
   * of parents that matches the search string
   *
   * @param projects
   * @param regex
   */
  private filterProjects = (projects: ProjectSummary[], search: string): ProjectSummary[] =>
    orderBy(
      compact(
        loMap(projects, (p) => {
          if (p.Name.toLocaleLowerCase().includes(search.toLowerCase()) || (p.Slug && p.Slug.toLocaleLowerCase().includes(search.toLowerCase()))) {
            // loop through this parent's projects and filter that as well
            if (p.Projects) {
              p.Projects = this.filterProjects(p.Projects, search);
            }
            return p;
          } else if (p.Projects) {
            const filteredList: ProjectSummary[] = this.filterProjects(p.Projects, search);
            if (isEmpty(filteredList)) {
              return null;
            }
            p.Projects = filteredList;
            return p;
          }
          return null;
        })
      ),
      'Name'
    );

  private async setScript(): Promise<any> {
    return await Promise.all([
      environment.analyticsScriptUrl ? this.injectScript(environment.analyticsScriptUrl) : Promise.resolve(),
      this.injectScript(environment.lfxHeader + '/lfx-header-v2.js'),
    ]);
  }

  private injectScript(src: string): Promise<any> {
    if (this.isScriptLoaded(src)) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = src;
      script.addEventListener('load', resolve);
      script.addEventListener('error', (e) => reject(e.error));
      document.head.appendChild(script);
    });
  }

  private isScriptLoaded(url: string): boolean {
    const scripts = document.getElementsByTagName('script');

    for (let i = scripts.length; i--; ) {
      if (scripts[i].src === url) {
        return true;
      }
    }

    return false;
  }

  private redirectToV1(path: string[], url: string): void {
    if (path.length > 3 && path[1] === 'project' && path[2] !== '') {
      const projectRegex = new RegExp('/(?:project)/(.*?)/(operations|collaboration|development|tools)/(ci|insights|security|easycla)(.*)');
      // Navigate to the new URL using window.location.href
      const match = url.match(projectRegex);
      if (match) {
        window.location.href = `${environment.originalSiteURL}/project/${match[1]}/${match[2]}/${match[3]}${match[4] ?? ''}`;
      }
    }
  }
}
